import { CogIcon, EyeIcon } from "@heroicons/react/24/outline";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import z from "zod";

import { ROLES } from "@/api/admins";
import type { Product } from "@/api/products";
import { ProductStatus } from "@/api/products";
import { QueryBuilderContext } from "@/contexts/query-builder-context";
import { useProduct } from "@/hooks/use-product";
import { useTableQueryBuilder } from "@/hooks/use-table-query-builder";
import { useAdminStore } from "@/stores";
import { Badge, BADGE_STATUS, Button, ScreenContent, ScreenHeader, ScreenTitle } from "@/ui";
import { Tooltip } from "@/ui/common";
import { PaginatedTable } from "@/ui/common/paginated-table";
import { HeaderSearchInput } from "@/ui/table/header-search-input";
import { SearchHeaderSelect } from "@/ui/table/search-header-select";
import { PRODUCT_STATUS_OPTIONS, PRODUCT_TYPE_OPTIONS, TCH_CBD_TYPE_OPTIONS } from "@/utils/consts";

const columns: ColumnDef<Product>[] = [
  {
    id: "name",
    header: (header) => {
      return <HeaderSearchInput header={header} name="Name" />;
    },
    accessorKey: "name",
    cell: ({ row }) => {
      const { images, name } = row.original;

      return (
        <div className="flex items-center">
          {images[0] ? <img alt={name} className="mr-2 size-8" src={images[0].url} /> : null}
          <span>{name}</span>
        </div>
      );
    },
  },
  {
    id: "type",
    header: (header) => {
      return <SearchHeaderSelect header={header} name="type" options={PRODUCT_TYPE_OPTIONS} />;
    },
    accessorKey: "type",
  },

  {
    id: "thcCbdType",
    header: (header) => {
      return (
        <SearchHeaderSelect header={header} name="tch_cbd_type" options={TCH_CBD_TYPE_OPTIONS} />
      );
    },
    accessorKey: "thcCbdType",
  },
  {
    id: "sku",
    header: (header) => {
      return <HeaderSearchInput header={header} name="Sku" />;
    },
    accessorKey: "sku",
  },
  {
    id: "status",
    header: (header) => {
      return <SearchHeaderSelect header={header} name="status" options={PRODUCT_STATUS_OPTIONS} />;
    },
    cell: ({ row }) => {
      const status = row.original.status;

      const badgeStatus =
        status === ProductStatus.active ? BADGE_STATUS.SUCCESS : BADGE_STATUS.DEFAULT;

      return (
        <Badge className="capitalize" status={badgeStatus}>
          {status}
        </Badge>
      );
    },

    accessorKey: "status",
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const { attributesUrl, detailsUrl } = row.original;

      return (
        <div className="flex gap-2">
          {detailsUrl ? (
            <Tooltip content="View in Shopify">
              <a className="hover:text-content-muted" href={detailsUrl}>
                <EyeIcon height={20} />
              </a>
            </Tooltip>
          ) : null}

          {attributesUrl ? (
            <Tooltip content="Edit in Shopify">
              <a className="hover:text-content-muted" href={attributesUrl}>
                <CogIcon height={20} />
              </a>
            </Tooltip>
          ) : null}
        </div>
      );
    },
  },
];

const PRODUCT_FILTER_KEYS = ["name", "type", "thcCbdType", "sku", "status"] as const;

const ProductsPage = () => {
  const { admin } = useAdminStore();

  const { queryBuilder, setCurrentPage, setSorting, sortingState } = useTableQueryBuilder({
    filterKeys: PRODUCT_FILTER_KEYS,
  });

  const { data: products, isLoading: isLoadingProducts } = useProduct().getProducts({
    queryBuilder,
  });

  const { isPending, mutate: syncProducts } = useProduct().syncProducts();

  return (
    <QueryBuilderContext.Provider value={{ builder: queryBuilder }}>
      <ScreenHeader>
        <ScreenTitle>Products</ScreenTitle>

        {admin?.role === ROLES.super && (
          <Button
            isLoading={isPending}
            onClick={() => {
              return syncProducts();
            }}
            variant="secondary"
          >
            Sync now
          </Button>
        )}
      </ScreenHeader>

      <ScreenContent>
        <PaginatedTable
          columns={columns}
          data={products?.data}
          isLoadingData={isLoadingProducts}
          pagination={products?.pagination}
          setCurrentPage={setCurrentPage}
          setSorting={setSorting}
          sorting={sortingState}
        />
      </ScreenContent>
    </QueryBuilderContext.Provider>
  );
};

export const Route = createFileRoute("/_private/products/")({
  component: ProductsPage,
  validateSearch: z.object({
    page: z.number().optional(),
    ...columns.reduce(
      (acc: Record<string, z.ZodOptional<z.ZodUnion<[z.ZodString, z.ZodNumber]>>>, column) => {
        if (column.id) {
          acc[column.id] = z.string().or(z.number().int()).optional();
        }

        return acc;
      },
      {},
    ),
  }),
});
